import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRtvs } from "@/features/vendor-returns/api";
import { RtvStatusBadge, VendorName } from "@/features/vendor-returns/bits";
import { fmtDateTime } from "@/lib/utils";
import { SearchInput } from "@/components/shared/SearchInput";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { normalizeSearch, searchEmptyMessage } from "@/lib/listSearch";

export const Route = createFileRoute("/_authenticated/vendor-returns/")({ component: RtvIndex });

function RtvIndex() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const term = useDebouncedValue(search);
  const { data: rows = [], isLoading, isError, error } = useRtvs({ status: status === "all" ? undefined : status, search: term });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Return to Vendor</h1>
          <p className="text-sm text-muted-foreground">Defective or rejected goods sent back to the vendor. Start a return from a GRN.</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
        <SearchInput value={search} onChange={setSearch} placeholder="Search RTV, GRN, PO, vendor or item" label="Search returns" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["Draft", "Dispatched", "Closed", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>RTV</TableHead><TableHead>Vendor</TableHead><TableHead>Source GRN</TableHead>
            <TableHead className="text-right">Lines</TableHead><TableHead className="text-right">Qty returned</TableHead>
            <TableHead>Status</TableHead><TableHead>Created</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">{normalizeSearch(term) ? "Searching…" : "Loading…"}</TableCell></TableRow>}
            {isError && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-red-600">{(error as Error).message}</TableCell></TableRow>}
            {!isLoading && !isError && rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">{searchEmptyMessage(term, "returns", "No returns yet.")}</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/vendor-returns/$rtvId", params: { rtvId: r.id } })}>
                <TableCell className="font-medium">{r.rtv_number}</TableCell>
                <TableCell><VendorName name={r.vendor_name} oneTime={r.vendor_is_one_time} /></TableCell>
                <TableCell><Link to="/grns/$grnId" params={{ grnId: r.grn }} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{r.grn_number}</Link></TableCell>
                <TableCell className="text-right tabular-nums">{r.items.length}</TableCell>
                <TableCell className="text-right tabular-nums">{r.items.reduce((s, i) => s + Number(i.quantity), 0)}</TableCell>
                <TableCell><RtvStatusBadge status={r.status} /></TableCell>
                <TableCell className="text-xs">{fmtDateTime(r.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
